"use client";

import * as React from "react";
import { Select } from "@base-ui/react/select";
import { ToggleGroup } from "@base-ui/react/toggle-group";
import { Toggle } from "@base-ui/react/toggle";
import { CheckIcon, ChevronDownIcon, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import {
    HOUR_OPTIONS,
    type Meridiem,
    displayMeridiem,
    from12h,
    minuteOptions,
    to12h,
} from "@/lib/timeOfDay";

export type TimeFieldProps = {
    id?: string;
    value: string;
    onValueChange: (next: string) => void;
    /** Meridiem to show while `value` is empty — pass the start time's so an
     *  evening event does not need the toggle touched twice. */
    inheritMeridiem?: Meridiem | null;
    disabled?: boolean;
    invalid?: boolean;
    /** Wired to `aria-describedby` so the inline error is announced. */
    describedBy?: string;
    className?: string;
    /** Whether to show quick presets (e.g., 9:00 AM, 2:00 PM, 6:00 PM). */
    showPresets?: boolean;
};

export const TRIGGER_CLASS =
    "flex-1 min-w-[4.5rem] inline-flex items-center justify-between gap-1 rounded-xl border border-input bg-background px-3 py-2.5 text-base font-semibold " +
    "outline-none transition-all select-none " +
    "hover:bg-accent/40 hover:border-teal-700/40 " +
    "focus-visible:border-teal-700 focus-visible:ring-3 focus-visible:ring-teal-700/25 " +
    "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted " +
    "aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20";

export const POPUP_CLASS =
    "z-50 max-h-64 overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white p-1.5 text-slate-900 shadow-xl outline-none scroll-smooth";

export const ITEM_CLASS =
    "flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-semibold outline-none select-none transition-colors " +
    "hover:bg-teal-50 hover:text-teal-900 data-highlighted:bg-[#003d45] data-highlighted:text-white";

/**
 * Animated SVG Clock Face showing hour and minute hands moving dynamically.
 */
function AnimatedClockFace({
    hour,
    minute,
    ampm,
}: {
    hour?: number;
    minute?: number;
    ampm?: Meridiem | null;
}) {
    const h = hour || 12;
    const m = minute || 0;
    // Hour hand rotates 30 deg per hour + 0.5 deg per minute
    const hourDeg = (h % 12) * 30 + m * 0.5;
    // Minute hand rotates 6 deg per minute
    const minDeg = m * 6;

    const isNight = ampm === "AM" && (h === 12 || h < 6);

    return (
        <div className="relative group shrink-0" title={`Clock: ${h}:${String(m).padStart(2, "0")} ${ampm || ""}`}>
            <svg
                className={cn(
                    "size-9 transition-transform duration-300 group-hover:scale-105 drop-shadow-xs",
                    isNight ? "text-amber-700" : "text-[#003d45]",
                )}
                viewBox="0 0 36 36"
                fill="none"
                aria-hidden="true"
            >
                {/* Dial background */}
                <circle
                    cx="18"
                    cy="18"
                    r="16"
                    className={cn(
                        "transition-colors duration-300 stroke-current",
                        isNight ? "fill-amber-50" : "fill-teal-50/50",
                    )}
                    strokeWidth="2.2"
                />

                {/* Major 12, 3, 6, 9 ticks */}
                <line x1="18" y1="4" x2="18" y2="6.5" stroke="#007a8a" strokeWidth="1.8" strokeLinecap="round" />
                <line x1="32" y1="18" x2="29.5" y2="18" stroke="#007a8a" strokeWidth="1.8" strokeLinecap="round" />
                <line x1="18" y1="32" x2="18" y2="29.5" stroke="#007a8a" strokeWidth="1.8" strokeLinecap="round" />
                <line x1="4" y1="18" x2="6.5" y2="18" stroke="#007a8a" strokeWidth="1.8" strokeLinecap="round" />

                {/* Hour hand */}
                <line
                    x1="18"
                    y1="18"
                    x2="18"
                    y2="9.5"
                    stroke="#003d45"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    transform={`rotate(${hourDeg} 18 18)`}
                    style={{ transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)" }}
                />

                {/* Minute hand */}
                <line
                    x1="18"
                    y1="18"
                    x2="18"
                    y2="6.5"
                    stroke="#007a8a"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    transform={`rotate(${minDeg} 18 18)`}
                    style={{ transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)" }}
                />

                {/* Center Pivot */}
                <circle cx="18" cy="18" r="2" className="fill-[#003d45]" />
            </svg>
        </div>
    );
}

/**
 * Returns contextual time-of-day information to eliminate AM/PM confusion.
 */
function getTimeOfDayInfo(value24?: string) {
    if (!value24 || !value24.includes(":")) return null;
    const [hStr, mStr] = value24.split(":");
    const h = Number(hStr);
    const m = Number(mStr);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;

    if (h >= 0 && h < 5) {
        return {
            label: "Late Night / Dawn",
            icon: "🌙",
            description: "Notice: Scheduled in the early morning hours (midnight to 5 AM). Did you mean PM?",
            isWarning: true,
            className: "bg-amber-50 text-amber-900 border-amber-300",
        };
    }
    if (h >= 5 && h < 12) {
        return {
            label: "Morning",
            icon: "🌅",
            description: "Morning event session",
            isWarning: false,
            className: "bg-sky-50 text-sky-800 border-sky-200",
        };
    }
    if (h >= 12 && h < 17) {
        return {
            label: "Afternoon",
            icon: "☀️",
            description: "Afternoon event session",
            isWarning: false,
            className: "bg-emerald-50 text-emerald-800 border-emerald-200",
        };
    }
    if (h >= 17 && h < 21) {
        return {
            label: "Evening",
            icon: "🌆",
            description: "Evening event session",
            isWarning: false,
            className: "bg-indigo-50 text-indigo-800 border-indigo-200",
        };
    }
    return {
        label: "Night",
        icon: "🌙",
        description: "Late evening event session",
        isWarning: false,
        className: "bg-purple-50 text-purple-800 border-purple-200",
    };
}

function UnitSelect({
    value,
    onValueChange,
    options,
    placeholder,
    disabled,
    invalid,
    describedBy,
    label,
    id,
}: {
    value: string;
    onValueChange: (next: string) => void;
    options: string[];
    placeholder: string;
    disabled?: boolean;
    invalid?: boolean;
    describedBy?: string;
    label: string;
    id?: string;
}) {
    return (
        <Select.Root
            value={value || null}
            onValueChange={(next) => onValueChange((next as string) ?? "")}
            disabled={disabled}
        >
            <Select.Trigger
                id={id}
                aria-label={label}
                aria-invalid={invalid || undefined}
                aria-describedby={describedBy}
                className={TRIGGER_CLASS}
            >
                <Select.Value placeholder={placeholder}>
                    {(v: string | null) => (
                        <span className={cn("font-mono font-bold", !v && "text-muted-foreground font-normal")}>
                            {v ? v : placeholder}
                        </span>
                    )}
                </Select.Value>
                <Select.Icon className="text-muted-foreground transition-transform duration-200">
                    <ChevronDownIcon className="size-4" />
                </Select.Icon>
            </Select.Trigger>
            <Select.Portal>
                <Select.Positioner sideOffset={4} className="z-50">
                    <Select.Popup className={POPUP_CLASS}>
                        <div className="px-2 py-1 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b border-slate-100 mb-1">
                            {label}
                        </div>
                        {options.map((option) => (
                            <Select.Item key={option} value={option} className={ITEM_CLASS}>
                                <Select.ItemText className="font-mono">{option}</Select.ItemText>
                                <Select.ItemIndicator>
                                    <CheckIcon className="size-4 text-emerald-600" />
                                </Select.ItemIndicator>
                            </Select.Item>
                        ))}
                    </Select.Popup>
                </Select.Positioner>
            </Select.Portal>
        </Select.Root>
    );
}

const COMMON_START_PRESETS = [
    { label: "9:00 AM", val: "09:00" },
    { label: "10:00 AM", val: "10:00" },
    { label: "12:00 PM", val: "12:00" },
    { label: "2:00 PM", val: "14:00" },
    { label: "5:30 PM", val: "17:30" },
    { label: "6:00 PM", val: "18:00" },
    { label: "7:00 PM", val: "19:00" },
];

export function TimeField({
    id,
    value,
    onValueChange,
    inheritMeridiem = null,
    disabled,
    invalid,
    describedBy,
    className,
    showPresets = false,
}: TimeFieldProps) {
    const parts = to12h(value);
    const shownMeridiem = displayMeridiem(value, inheritMeridiem);

    // The meridiem actually used when building a value from a fresh pick.
    const workingMeridiem: Meridiem = shownMeridiem ?? "AM";

    const emit = (h: string, m: string, ampm: Meridiem) => {
        if (!h) {
            onValueChange("");
            return;
        }
        onValueChange(from12h(h, m || "00", ampm));
    };

    const timeOfDay = getTimeOfDayInfo(value);
    const currentMinutesList = minuteOptions(parts.m);

    return (
        <div className={cn("flex flex-col gap-1.5", className)}>
            {/* Unified time input row */}
            <div
                className={cn(
                    "flex flex-wrap items-center gap-2 p-1.5 bg-white rounded-2xl border transition-all shadow-xs min-w-0",
                    invalid
                        ? "border-destructive/80 ring-2 ring-destructive/20"
                        : "border-slate-200 hover:border-teal-700/40 focus-within:border-teal-700 focus-within:ring-2 focus-within:ring-teal-700/20",
                    disabled && "opacity-60 bg-slate-50 cursor-not-allowed",
                )}
            >
                {/* Dynamic animated SVG clock face */}
                <div className="pl-1">
                    <AnimatedClockFace
                        hour={parts.h ? Number(parts.h) : undefined}
                        minute={parts.m ? Number(parts.m) : undefined}
                        ampm={shownMeridiem}
                    />
                </div>

                {/* Hour selection */}
                <UnitSelect
                    id={id}
                    label="Hour"
                    value={parts.h}
                    options={HOUR_OPTIONS}
                    placeholder="HH"
                    disabled={disabled}
                    invalid={invalid}
                    describedBy={describedBy}
                    onValueChange={(h) => emit(h, parts.m || "00", workingMeridiem)}
                />

                <span aria-hidden className="font-bold text-muted-foreground font-mono text-lg">
                    :
                </span>

                {/* Minute selection */}
                <UnitSelect
                    label="Minute"
                    value={parts.m}
                    options={currentMinutesList}
                    placeholder="MM"
                    disabled={disabled}
                    invalid={invalid}
                    describedBy={describedBy}
                    onValueChange={(m) => emit(parts.h || "12", m, workingMeridiem)}
                />

                {/* AM/PM Toggle */}
                <ToggleGroup
                    value={shownMeridiem ? [shownMeridiem] : []}
                    onValueChange={(next: unknown[]) => {
                        const picked = (next as Meridiem[])[0];
                        if (!picked) return; // never let the pair become unset
                        emit(parts.h, parts.m || "00", picked);
                    }}
                    aria-label="AM or PM"
                    className="inline-flex shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/70 p-0.5"
                >
                    {(["AM", "PM"] as const).map((ap) => (
                        <Toggle
                            key={ap}
                            value={ap}
                            disabled={disabled}
                            aria-invalid={invalid || undefined}
                            className={cn(
                                "px-3 py-2 text-xs font-bold rounded-lg outline-none transition-all select-none",
                                "text-muted-foreground hover:bg-teal-50 hover:text-teal-900",
                                "focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:relative",
                                "disabled:cursor-not-allowed disabled:opacity-50",
                                "data-pressed:bg-[#003d45] data-pressed:text-white data-pressed:shadow-xs",
                            )}
                        >
                            {ap}
                        </Toggle>
                    ))}
                </ToggleGroup>
            </div>

            {/* Contextual Time-of-Day Pill (Prevents AM/PM scheduling mistakes) */}
            {timeOfDay && (
                <div
                    className={cn(
                        "flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all animate-in fade-in slide-in-from-top-1 duration-200",
                        timeOfDay.className,
                    )}
                >
                    <span className="text-sm">{timeOfDay.icon}</span>
                    <span>
                        <strong>{timeOfDay.label}</strong> ({parts.h}:{parts.m} {shownMeridiem})
                    </span>
                    {timeOfDay.isWarning && (
                        <span className="ml-auto text-[11px] font-bold text-amber-900 underline decoration-amber-500">
                            Check AM/PM
                        </span>
                    )}
                </div>
            )}

            {/* Quick Presets for start times */}
            {showPresets && (
                <div className="flex flex-wrap items-center gap-1 mt-0.5">
                    <span className="text-[11px] font-bold text-muted-foreground flex items-center gap-1 mr-0.5">
                        <Sparkles className="size-3 text-teal-600" /> Popular:
                    </span>
                    {COMMON_START_PRESETS.map((preset) => {
                        const active = value === preset.val;
                        return (
                            <button
                                key={preset.val}
                                type="button"
                                disabled={disabled}
                                onClick={() => onValueChange(preset.val)}
                                className={cn(
                                    "px-2 py-0.5 rounded-full text-xs font-semibold border transition-all cursor-pointer",
                                    active
                                        ? "bg-[#003d45] text-white border-[#003d45]"
                                        : "bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-900",
                                )}
                            >
                                {preset.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default TimeField;
